import React, { useEffect, useMemo, useState } from 'react';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useClub } from '@/contexts/ClubContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { ListOrdered, CheckCircle, Users, Timer, Plus, X, Search } from 'lucide-react';
import StarRating from '@/components/StarRating';
import { Court, Match, Member } from '@/types';
import { DndContext, DragEndEvent, DragOverlay, PointerSensor, TouchSensor, useDraggable, useDroppable, useSensor, useSensors } from '@dnd-kit/core';
import { validateBadmintonFinalScore } from '@/lib/scoreValidation';
import PageSpinner from '@/components/PageSpinner';
import { CardGridSkeleton, PageHeaderSkeleton, SectionCardSkeleton } from '@/components/PageSkeletons';
import { Skeleton } from '@/components/ui/skeleton';
import { API_BASE as API_BASE_URL } from '@/services/apiClient';
import {
  clubService,
  billingService,
  courtService,
  equipmentService,
  feedbackService,
  matchService,
  memberService,
  queueService,
} from '@/services/storageService';
import {
  mapMatchHistoryFromApi,
  mapMembersFromApi,
  mapQueueFromApi,
  getLatestEvaluation,
  mapEvaluationsFromApi,
  mapRacketsToEquipment,
  mapShoesToEquipment,
} from '@/lib/clubApiMappers';
import { buildAvatarDataUri } from '@/lib/avatarBuilder';
import { useI18n } from '@/contexts/I18nContext';
import './QueueAndCourts.css';

const API_BASE = API_BASE_URL;

function clampInt(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, Math.trunc(n)));
}

function scoreStrToInt(v: string) {
  const s = v.trim();
  if (s === '') return 0;
  const n = Number(s);
  if (!Number.isFinite(n)) return 0;
  return clampInt(n, 0, 30);
}

const renderStars = (count?: number) => <StarRating value={count ?? 0} size={14} />;

const getMemberRating = (member?: Member | null, evaluationsEnabled = true) => {
  if (!member) return undefined;
  if (!evaluationsEnabled) return 0;
  const hasEvaluation =
    member.role === 'coach' || member.hasEvaluation || (member.evaluations && member.evaluations.length > 0);
  if (!hasEvaluation) return undefined;
  const latest = getLatestEvaluation(member.evaluations);

  if (member.role === 'coach') return latest?.rating ?? 5;
  return latest?.rating;
};

const getInitials = (first?: string | null, last?: string | null) => {
  const firstChar = (first || '').trim().charAt(0);
  const lastChar = (last || '').trim().charAt(0);
  const combined = `${firstChar}${lastChar}`.toUpperCase();
  return combined || '?';
};

const resolveAvatarUrl = (member?: Member | null) => {
  if (!member) return undefined;
  return member.avatarUrl || buildAvatarDataUri(member.avatarCollection, member.avatarSeed);
};

const normalizeSelectionKey = (value?: string | null) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const lower = raw.toLowerCase();
  if (lower === 'round-robin' || lower === 'round_robin') return 'ROUND_ROBIN';
  if (lower === 'skill-based' || lower === 'skill_based') return 'SKILL_BASED';
  if (lower === 'balanced' || lower === 'balanced_team') return 'BALANCED_TEAM';
  if (lower === 'players-pick' || lower === 'players_pick') return 'PLAYERS_PICK';
  return raw.toUpperCase();
};

const selectionLabelFromValue = (value?: string | null) => {
  const key = normalizeSelectionKey(value);
  if (key === 'ROUND_ROBIN') return 'Round Robin';
  if (key === 'SKILL_BASED') return 'Skill Based';
  if (key === 'BALANCED_TEAM') return 'Balanced';
  if (key === 'PLAYERS_PICK') return 'Players Pick';
  return null;
};

const normalizeTeamIds = (ids: Array<string | number>) =>
  Array.from(new Set(ids.map((id) => String(id)))).sort().join('|');

const buildCourtKey = (match: any) => {
  if (match?.courtId) return `court:${match.courtId}`;
  if (match?.courtNumber) return `court-num:${match.courtNumber}`;
  return 'court:unknown';
};

const resolveMatchTeams = (match: any) => {
  const players = Array.isArray(match?.players) ? match.players : [];
  const team1 = Array.isArray(match?.team1) && match.team1.length ? match.team1 : players.slice(0, 2);
  const team2 = Array.isArray(match?.team2) && match.team2.length ? match.team2 : players.slice(2, 4);
  return { team1, team2 };
};

type CourtDragMeta = {
  kind: 'swap';
  courtId: string;
  playerId: string;
  team: number;
};

type QueuePickMeta = {
  kind: 'pick';
  memberId: string;
};

type CourtPickTargetMeta = {
  kind: 'pick-target';
  courtId: string;
};

const CourtPlayerChip = ({
  courtId,
  player,
  team,
  canDrag,
  canManageQueue,
  ratingValue,
  onRemove,
}: {
  courtId: string;
  player: Member;
  team: number;
  canDrag: boolean;
  canManageQueue: boolean;
  ratingValue: number;
  onRemove: () => void;
}) => {
  const id = `court:${courtId}:player:${player.id}`;
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
    data: { kind: 'swap', courtId, playerId: player.id, team } satisfies CourtDragMeta,
    disabled: !canDrag,
  });
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id,
    data: { kind: 'swap', courtId, playerId: player.id, team } satisfies CourtDragMeta,
  });
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;
  const mergedRef = (node: HTMLElement | null) => {
    setNodeRef(node);
    setDropRef(node);
  };

  return (
    <div
      ref={mergedRef}
      style={style}
      className={`text-sm rounded py-1 pr-6 relative flex items-center justify-center gap-2 flex-nowrap court-player-chip ${
        canDrag ? 'cursor-grab active:cursor-grabbing touch-none' : ''
      } ${isDragging ? 'opacity-70' : ''} ${isOver ? 'ring-2 ring-primary/50' : ''}`}
      {...attributes}
      {...listeners}
    >
      <div className="flex items-center justify-center gap-1 min-w-0 whitespace-nowrap">
        <Avatar className="h-6 w-6 shrink-0">
          {resolveAvatarUrl(player) && (
            <AvatarImage
              src={resolveAvatarUrl(player)}
              alt={player.avatarName || `${player.firstName} ${player.lastName}`}
            />
          )}
          <AvatarFallback className="text-[10px]">
            {getInitials(player.firstName, player.lastName)}
          </AvatarFallback>
        </Avatar>
        <span className="truncate court-player-name">
          {player.firstName} {player.lastName[0]}.
        </span>
      </div>

      {canManageQueue && (
        <button
          type="button"
          className="absolute right-1 top-1 inline-flex items-center justify-center h-7 w-7 rounded-full text-destructive hover:bg-destructive/10 shrink-0"
          onClick={onRemove}
          aria-label="Remove from court"
        >
          <X size={16} />
        </button>
      )}

      <span className="flex items-center justify-center gap-1 text-[11px] text-muted-foreground shrink-0 court-player-stars">
        {renderStars(ratingValue)}
      </span>
    </div>
  );
};

const CourtPlayerChipOverlay = ({ player }: { player: Member }) => (
  <div className="text-sm rounded py-1 px-3 flex items-center justify-center gap-2 flex-nowrap court-player-chip drag-chip bg-primary/15 shadow-lg ring-2 ring-primary/40">
    <Avatar className="h-6 w-6 shrink-0">
      {resolveAvatarUrl(player) && (
        <AvatarImage src={resolveAvatarUrl(player)} alt={player.avatarName || `${player.firstName} ${player.lastName}`} />
      )}
      <AvatarFallback className="text-[10px]">{getInitials(player.firstName, player.lastName)}</AvatarFallback>
    </Avatar>
    <span className="truncate court-player-name">
      {player.firstName} {player.lastName[0]}.
    </span>
  </div>
);

const QueuePickItem = ({
  entryId,
  member,
  canDrag,
  children,
}: {
  entryId: string;
  member: Member | undefined;
  canDrag: boolean;
  children: React.ReactNode;
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `queue:${entryId}`,
    data: { kind: 'pick', memberId: entryId } satisfies QueuePickMeta,
    disabled: !canDrag,
  });
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={canDrag ? 'cursor-grab active:cursor-grabbing touch-none' : undefined}
      {...attributes}
      {...listeners}
    >
      {children}
    </div>
  );
};

const CourtPickDropZone = ({
  courtId,
  isActive,
  children,
}: {
  courtId: string;
  isActive: boolean;
  children: React.ReactNode;
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `court-pick:${courtId}`,
    data: { kind: 'pick-target', courtId } satisfies CourtPickTargetMeta,
    disabled: !isActive,
  });
  return (
    <div
      ref={setNodeRef}
      className={
        isActive
          ? `court-pick-zone rounded-lg border-2 border-dashed ${isOver ? 'border-primary ring-2 ring-primary/30 bg-primary/5' : 'border-muted'}`
          : ''
      }
    >
      {children}
    </div>
  );
};

export default function QueueAndCourts() {
  const { t } = useI18n();
  const { user, token } = useAuth();
  const { activeClubId } = useClub();
  const queryClient = useQueryClient();
  const clubId = activeClubId ? Number(activeClubId) : null;

  const clubQuery = useQuery({
    queryKey: ['club', clubId],
    queryFn: () => clubService.get(clubId as number),
    enabled: Boolean(clubId),
  });

  const membersQuery = useQuery({
    queryKey: ['club-members', clubId],
    queryFn: () => memberService.list(clubId as number),
    enabled: Boolean(clubId),
  });

  const queueQuery = useQuery({
    queryKey: ['club-queue', clubId],
    queryFn: () => queueService.list(clubId as number),
    enabled: Boolean(clubId),
  });

  const historyQuery = useQuery({
    queryKey: ['match-history', clubId],
    queryFn: () => matchService.listHistoryByClub(clubId as number),
    enabled: Boolean(clubId),
  });

  const billingQuery = useQuery({
    queryKey: ['billing-status', clubId],
    queryFn: () => billingService.getStatus(clubId),
    enabled: Boolean(user && clubId),
  });
  const resolvedPackageCode = String(
    billingQuery.data?.package || billingQuery.data?.recommended_package || 'package1',
  )
    .trim()
    .toLowerCase();
  const isEvaluationsFeatureAvailable = resolvedPackageCode !== 'package1';
  const isEquipmentFeatureAvailable = resolvedPackageCode !== 'package1';

  const shoesQuery = useQuery({
    queryKey: ['equipment-shoes'],
    queryFn: () => equipmentService.listShoes(),
    enabled: Boolean(user) && isEquipmentFeatureAvailable,
  });

  const racketsQuery = useQuery({
    queryKey: ['equipment-rackets'],
    queryFn: () => equipmentService.listRackets(),
    enabled: Boolean(user) && isEquipmentFeatureAvailable,
  });

  const queue = mapQueueFromApi(queueQuery.data as any[]);
  const baseMembers = mapMembersFromApi(membersQuery.data as any[], queueQuery.data as any[]);

  const currentMemberBase = user
    ? baseMembers.find((m) => m.userId === String(user.id) || m.id === String(user.id))
    : undefined;
  const canViewEvals = isEvaluationsFeatureAvailable;

  const evalsQuery = useQuery({
    queryKey: ['club-evals', clubId],
    queryFn: () => feedbackService.listForClub(clubId as number),
    enabled: Boolean(clubId) && canViewEvals,
  });

  const evalsByMember = useMemo(() => {
    const grouped: Record<string, any[]> = {};
    (evalsQuery.data || []).forEach((ev: any) => {
      const id = String(ev.player_id ?? ev.playerId ?? '');
      if (!id) return;
      if (!grouped[id]) grouped[id] = [];
      grouped[id].push(ev);
    });
    return grouped;
  }, [evalsQuery.data]);

  const members = useMemo(() => {
    if (!user?.id) return baseMembers;
    const shoes = mapShoesToEquipment(shoesQuery.data as any[]);
    const rackets = mapRacketsToEquipment(racketsQuery.data as any[]);
    return baseMembers.map((m) =>
      m.id === String(user.id) || m.userId === String(user.id)
        ? {
            ...m,
            equipment: isEquipmentFeatureAvailable ? [...shoes, ...rackets] : [],
            evaluations: isEvaluationsFeatureAvailable ? mapEvaluationsFromApi(evalsByMember[m.id] || []) : [],
            hasEvaluation: isEvaluationsFeatureAvailable
              ? m.hasEvaluation || (evalsByMember[m.id] || []).length > 0
              : false,
          }
        : {
            ...m,
            evaluations: isEvaluationsFeatureAvailable ? mapEvaluationsFromApi(evalsByMember[m.id] || []) : [],
            hasEvaluation: isEvaluationsFeatureAvailable
              ? m.hasEvaluation || (evalsByMember[m.id] || []).length > 0
              : false,
          },
    );
  }, [
    baseMembers,
    shoesQuery.data,
    racketsQuery.data,
    user?.id,
    evalsByMember,
    isEquipmentFeatureAvailable,
    isEvaluationsFeatureAvailable,
  ]);
  const matchHistory = mapMatchHistoryFromApi(historyQuery.data as any[], members);

  const club = clubQuery.data as any;
  const selectionMode = useMemo(() => {
    const mechanism = String(club?.selection_mechanism || '').toUpperCase();
    if (mechanism === 'SKILL_BASED') return 'skill-based';
    if (mechanism === 'BALANCED_TEAM') return 'balanced';
    if (mechanism === 'PLAYERS_PICK') return 'players-pick';
    return 'round-robin';
  }, [club]);
  const isPlayersPick = selectionMode === 'players-pick';
  const allowDragSwap = club?.allow_drag_swap ?? true;
  const allowQueueShuffle = club?.allow_queue_shuffle ?? true;
  const approxPlayers = typeof club?.approx_num_players === 'number' ? club.approx_num_players : null;

  const ongoingMatches = matchHistory.filter((m) => m.status === 'ongoing' || m.isActive);
  const baseCourts: Court[] = (() => {
    const count = Number(club?.num_courts || 0);
    if (Array.isArray(club?.courts) && club.courts.length) {
      const byNumber = new Map<number, any>();
      club.courts.forEach((court: any) => {
        const num = Number(court.court_number);
        if (Number.isFinite(num)) byNumber.set(num, court);
      });
      return Array.from({ length: count }, (_v, idx) => {
        const number = idx + 1;
        const court = byNumber.get(number);
        return {
          id: court ? String(court.id) : `virtual-${number}`,
          number,
          players: [],
          team1: [],
          team2: [],
          isActive: false,
        };
      });
    }
    return Array.from({ length: count }, (_v, idx) => ({
      id: `virtual-${idx + 1}`,
      number: idx + 1,
      players: [],
      team1: [],
      team2: [],
      isActive: false,
    }));
  })();

  const courts = baseCourts.map((court) => {
    const activeMatch = ongoingMatches.find((m) => {
      if (m.courtId) return m.courtId === court.id;
      return String(m.courtNumber) === String(court.number);
    });
    if (!activeMatch) return court;
    return {
      ...court,
      isActive: true,
      players: activeMatch.players,
      team1: activeMatch.team1,
      team2: activeMatch.team2,
      matchStartTime: activeMatch.timestamp,
    };
  });

  const matches = ongoingMatches;

  const freeCourtInfo = useMemo(() => {
    if (!courts.length) return { courtId: null as string | null, freeSince: null as number | null };
    const finishedMatches = matchHistory.filter((m) => m.status === 'finished' || !!m.finishedAt);
    const freeCourts = courts.filter((c) => !c.isActive);
    if (!freeCourts.length) return { courtId: null, freeSince: null };
    let selectedId: string | null = freeCourts[0]?.id ?? null;
    let oldest: number | null = null;
    freeCourts.forEach((court) => {
      const lastMatch = finishedMatches
        .filter((m) => String(m.courtId || m.courtNumber) === String(court.id || court.number))
        .sort((a, b) => new Date(b.finishedAt || b.timestamp || 0).getTime() - new Date(a.finishedAt || a.timestamp || 0).getTime())[0];
      const finishedAt = lastMatch?.finishedAt || lastMatch?.timestamp;
      if (!finishedAt) return;
      const ts = new Date(finishedAt).getTime();
      if (oldest === null || ts < oldest) {
        oldest = ts;
        selectedId = court.id;
      }
    });
    return { courtId: selectedId, freeSince: oldest };
  }, [courts, matchHistory]);

  const getMemberById = (id: string) => members.find((m) => m.id === id || m.userId === id);
  const getMemberByUserId = (userId: string) =>
    members.find((m) => m.userId === userId || m.id === userId || m.email?.toLowerCase() === (user?.email || '').toLowerCase());

  const setPlayerAvailable = async (memberId: string, available: boolean) => {
    if (!clubId) return;
    if (available) {
      await queueService.markAvailable(clubId, memberId);
    } else {
      await queueService.remove(clubId, memberId);
    }
    await queryClient.invalidateQueries({ queryKey: ['club-queue', clubId] });
  };

  const assignPlayersToCourt = async (courtId: string) => {
    if (!clubId) return null;
    const res = await courtService.assignPlayers(clubId, courtId);
    await queryClient.invalidateQueries({ queryKey: ['match-history', clubId] });
    await queryClient.invalidateQueries({ queryKey: ['club-queue', clubId] });
    return res;
  };

  const finishMatch = async (
    courtId: string,
    score: { team1: number; team2: number },
    equipmentUsedList: Match['equipmentUsed'],
    notes?: string,
  ) => {
    const numericKey = Number(courtId);
    const activeMatch =
      ongoingMatches.find((m) => m.courtId === courtId) ||
      (Number.isFinite(numericKey) ? ongoingMatches.find((m) => m.courtNumber === numericKey) : undefined);
    if (!activeMatch) return;
    await matchService.finish(activeMatch.id, {
      team1_score: String(score.team1),
      team2_score: String(score.team2),
      notes,
      players: activeMatch.players.map((pid) => {
        const equipment = (equipmentUsedList || []).find((eq) => String(eq.memberId) === String(pid));
        return {
          player_id: Number(pid),
          racket_id: equipment?.racket ? Number(equipment.racket) : undefined,
          shoe_id: equipment?.shoes ? Number(equipment.shoes) : undefined,
        };
      }),
    });
    await queryClient.invalidateQueries({ queryKey: ['match-history', clubId] });
    await queryClient.invalidateQueries({ queryKey: ['club-queue', clubId] });
  };

  const clearQueue = async () => {
    if (!clubId) return;
    await queueService.clear(clubId);
    await queryClient.invalidateQueries({ queryKey: ['club-queue', clubId] });
  };

  const shuffleQueue = async () => {
    if (!clubId) return;
    await queueService.shuffle(clubId);
    await queryClient.invalidateQueries({ queryKey: ['club-queue', clubId] });
  };

  const [finishDialogOpen, setFinishDialogOpen] = useState(false);
  const [selectedCourt, setSelectedCourt] = useState<Court | null>(null);

  const [scoreInput, setScoreInput] = useState<{ team1: string; team2: string }>({ team1: '21', team2: '21' });

  const [notes, setNotes] = useState('');
  const [equipmentUsed, setEquipmentUsed] = useState<Match['equipmentUsed']>([]);
  const [equipmentOwnerId, setEquipmentOwnerId] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [pendingAdds, setPendingAdds] = useState<
    Record<string, { firstName: string; lastName: string; status: 'pending' | 'confirmed' }>
  >({});
  const [pendingRemovals, setPendingRemovals] = useState<Set<string>>(new Set());
  const [clearingQueue, setClearingQueue] = useState(false);

  const [courtPage, setCourtPage] = useState(1);
  const courtsPerPage = 6;
  const [removeConfirm, setRemoveConfirm] = useState<{ memberId: string; name: string } | null>(null);
  const [removeCourtConfirm, setRemoveCourtConfirm] = useState<{
    memberId: string;
    name: string;
    courtId: string;
  } | null>(null);
  const [queueActionConfirm, setQueueActionConfirm] = useState<'clear' | 'shuffle' | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removingFromCourt, setRemovingFromCourt] = useState(false);
  const [queueActionLoading, setQueueActionLoading] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [finishDots, setFinishDots] = useState('.');
  const [finishingCourtId, setFinishingCourtId] = useState<string | null>(null);
  const [assigningCourts, setAssigningCourts] = useState<Set<string>>(new Set());
  const [assignDots, setAssignDots] = useState('.');
  const [queueSearch, setQueueSearch] = useState('');
  const [addSearch, setAddSearch] = useState('');
  const [activeDrag, setActiveDrag] = useState<CourtDragMeta | QueuePickMeta | null>(null);
  const [selectedPickIds, setSelectedPickIds] = useState<string[]>([]);
  const [pickCourtId, setPickCourtId] = useState<string | null>(null);
  const [isAssigningPick, setIsAssigningPick] = useState(false);
  const [isMobilePick, setIsMobilePick] = useState(false);

  const currentMember = user ? getMemberByUserId(user.id) : undefined;
  const canManageQueue = currentMember?.role === 'admin' || currentMember?.role === 'coach';
  const canDragSwap = canManageQueue && allowDragSwap;
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
  );
  const isSelf = (memberId: string) => !!(currentMember && String(currentMember.id) === String(memberId));
  const effectiveQueue = React.useMemo(() => {
    if (clearingQueue) return [];
    const base = queue.filter((entry) => !pendingRemovals.has(entry.userId));
    const pendingExtras = Object.entries(pendingAdds)
      .filter(([id, meta]) => meta.status === 'confirmed' && !base.some((q) => q.userId === id))
      .map(([id, meta]) => ({
        userId: id,
        firstName: meta.firstName,
        lastName: meta.lastName,
        position: base.length + 1,
        status: 'QUEUED' as const,
        waitStartedAt: new Date().toISOString(),
        elapsedSeconds: 0,
        lastQueueSeconds: 0,
        priorityCandidate: false,
      }));
    return [...base, ...pendingExtras];
  }, [queue, pendingRemovals, clearingQueue, pendingAdds]);

  const queuedEntries = React.useMemo(
    () => effectiveQueue.filter((entry) => entry.status === 'QUEUED'),
    [effectiveQueue],
  );

  const orderedQueue = React.useMemo(
    () =>
      [...queuedEntries].sort(
        (a, b) => (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER),
      ),
    [queuedEntries],
  );
  const topQueueId = orderedQueue[0]?.userId ?? null;

  const pickTimeoutMs = 5 * 60 * 1000;
  const pickerId = React.useMemo(() => {
    if (!orderedQueue.length) return null;
    if (!freeCourtInfo.freeSince) return orderedQueue[0].userId;
    const timedOut = Date.now() - freeCourtInfo.freeSince > pickTimeoutMs;
    if (timedOut && orderedQueue[1]) return orderedQueue[1].userId;
    return orderedQueue[0].userId;
  }, [orderedQueue, freeCourtInfo.freeSince]);
  const currentUserId = currentMember?.id ?? (user ? String(user.id) : null);
  const canPickPlayers =
    isPlayersPick &&
    (canManageQueue || (currentUserId && pickerId && String(currentUserId) === String(pickerId)));
  const pickerMember = pickerId ? getMemberById(pickerId) : undefined;
  const pickCourtNumber = pickCourtId ? courts.find((court) => court.id === pickCourtId)?.number : undefined;
  const pickTimedOut = freeCourtInfo.freeSince ? now - freeCourtInfo.freeSince > pickTimeoutMs : false;
  const canSkipTop =
    isPlayersPick &&
    !!pickCourtId &&
    orderedQueue.length > 0 &&
    (canManageQueue || (currentUserId && topQueueId && String(currentUserId) === String(topQueueId)));

  useEffect(() => {
    if (!isPlayersPick) return;
    setPickCourtId(freeCourtInfo.courtId);
    setSelectedPickIds(pickerId ? [pickerId] : []);
  }, [isPlayersPick, freeCourtInfo.courtId, pickerId]);

  useEffect(() => {
    if (!isPlayersPick) return;
    if (!clubId || !pickCourtId) return;
    if (selectedPickIds.length !== 4) return;
    if (isAssigningPick) return;
    setIsAssigningPick(true);
    (async () => {
      try {
        await courtService.assignPlayersPick(clubId, pickCourtId, selectedPickIds);
        await queryClient.invalidateQueries({ queryKey: ['match-history', clubId] });
        await queryClient.invalidateQueries({ queryKey: ['club-queue', clubId] });
        toast.success('Players assigned to court');
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to assign players';
        toast.error(message);
      } finally {
        setIsAssigningPick(false);
        setSelectedPickIds(pickerId ? [pickerId] : []);
      }
    })();
  }, [isPlayersPick, clubId, pickCourtId, selectedPickIds, isAssigningPick, queryClient, pickerId]);

  const filteredQueue = React.useMemo(() => {
    const term = queueSearch.trim().toLowerCase();
    const baseQueue = isPlayersPick ? orderedQueue.filter((entry) => !selectedPickIds.includes(entry.userId)) : orderedQueue;
    if (!term) return baseQueue;
    return baseQueue.filter((entry) => {
      const member = getMemberById(entry.userId);
      const name = `${member?.firstName || ''} ${member?.lastName || ''}`.toLowerCase();
      const pendingName = `${entry.firstName || ''} ${entry.lastName || ''}`.toLowerCase();
      return name.includes(term) || pendingName.includes(term);
    });
  }, [orderedQueue, queueSearch, getMemberById, isPlayersPick, selectedPickIds]);

  const isRoundRobin = selectionMode === 'round-robin';
  const isSkillBased = selectionMode === 'skill-based';
  const isBalanced = selectionMode === 'balanced';
  const selectionLabel = React.useMemo(() => {
    const label = selectionLabelFromValue(selectionMode);
    return label ? `Selection Criteria: ${label}` : 'Selection Criteria: Not set';
  }, [selectionMode]);
  const selectionName = selectionLabelFromValue(selectionMode) || 'Not set';

  const roundRobinPoolIds = React.useMemo(
    () => (isRoundRobin ? orderedQueue.slice(0, 6).map((entry) => entry.userId) : []),
    [isRoundRobin, orderedQueue],
  );

  const queueLimit = approxPlayers;
  const queueLimitReached =
    queueLimit !== null && queuedEntries.length + Object.keys(pendingAdds).length >= queueLimit;

  const queuedIdSet = React.useMemo(
    () => new Set(queuedEntries.map((entry) => entry.userId)),
    [queuedEntries],
  );

  const availableMembers = members.filter(
    (m) => !queuedIdSet.has(m.id) && !pendingAdds[m.id] && !m.isPlaying && m.isActive !== false,
  );
  const selfAddCandidate =
    !canManageQueue &&
      currentMember &&
      !queuedIdSet.has(currentMember.id) &&
      !pendingAdds[currentMember.id] &&
      !currentMember.isAvailable &&
      !currentMember.isPlaying &&
      currentMember.isActive !== false
      ? currentMember
      : null;
  const addableMembers = canManageQueue ? availableMembers : selfAddCandidate ? [selfAddCandidate] : [];
  const filteredAddableMembers = React.useMemo(() => {
    const term = addSearch.trim().toLowerCase();
    if (!term) return addableMembers;
    return addableMembers.filter((m) => {
      const name = `${m.firstName || ''} ${m.lastName || ''}`.toLowerCase();
      return name.includes(term);
    });
  }, [addSearch, addableMembers]);

  useEffect(() => {
    if (!clubId || !API_BASE) return;
    if (!token) return;

    const url = new URL('/realtime/stream', API_BASE);
    url.searchParams.set('club_id', String(clubId));

    let closed = false;
    let retryTimer: number | null = null;
    let controller: AbortController | null = null;

    const notifyUpdate = () => {
      queryClient.invalidateQueries({ queryKey: ['club-queue', clubId] });
      queryClient.invalidateQueries({ queryKey: ['match-history', clubId] });
    };

    const readStream = async (res: Response) => {
      const reader = res.body?.getReader();
      if (!reader) throw new Error('Realtime stream unavailable');
      const decoder = new TextDecoder();
      let buffer = '';
      while (!closed) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx = buffer.indexOf('\n\n');
        while (idx !== -1) {
          const chunk = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          if (chunk.trim().length > 0 && chunk.split('\n').some((line) => line.startsWith('data:'))) {
            notifyUpdate();
          }
          idx = buffer.indexOf('\n\n');
        }
      }
    };

    const connect = async () => {
      if (closed) return;
      controller = new AbortController();
      try {
        const res = await fetch(url.toString(), {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (!res.ok) {
          throw new Error(`Realtime stream error: ${res.status}`);
        }
        await readStream(res);
      } catch (err) {
        if (closed) return;
        if (retryTimer) window.clearTimeout(retryTimer);
        retryTimer = window.setTimeout(connect, 1500);
      }
    };

    connect();

    return () => {
      closed = true;
      if (retryTimer) window.clearTimeout(retryTimer);
      try {
        controller?.abort();
      } catch { }
      controller = null;
    };
  }, [clubId, queryClient, token]);

  React.useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  React.useEffect(() => {
    const update = () => setIsMobilePick(window.matchMedia('(max-width: 1024px)').matches);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  React.useEffect(() => {
    if (!queue.length) return;
    setPendingAdds((prev) => {
      let changed = false;
      const next = { ...prev };
      queue.forEach((entry) => {
        if (next[entry.userId]) {
          delete next[entry.userId];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [queue]);

  React.useEffect(() => {
    if (!finishing) {
      setFinishDots('.');
      return;
    }
    const dots = ['.', '..', '...'];
    let idx = 0;
    const id = window.setInterval(() => {
      idx = (idx + 1) % dots.length;
      setFinishDots(dots[idx]);
    }, 400);
    return () => window.clearInterval(id);
  }, [finishing]);

  React.useEffect(() => {
    if (!assigningCourts.size) {
      setAssignDots('.');
      return;
    }
    const dots = ['.', '..', '...'];
    let idx = 0;
    const id = window.setInterval(() => {
      idx = (idx + 1) % dots.length;
      setAssignDots(dots[idx]);
    }, 400);
    return () => window.clearInterval(id);
  }, [assigningCourts]);

  useEffect(() => {
    if (!assigningCourts.size) return;
    const activeCourtIds = new Set(
      matches
        .map((m: any) => {
          if (m.courtId) return String(m.courtId);
          if (Number.isFinite(m.courtNumber)) return String(m.courtNumber);
          return '';
        })
        .filter(Boolean),
    );
    if (!activeCourtIds.size) return;
    setAssigningCourts((prev) => {
      const next = new Set(prev);
      let changed = false;
      activeCourtIds.forEach((id) => {
        if (next.delete(id)) changed = true;
      });
      return changed ? next : prev;
    });
  }, [matches, assigningCourts]);

  useEffect(() => {
    if (!finishingCourtId) return;
    const stillActive = matches.some((m: any) => {
      if (m.courtId && String(m.courtId) === finishingCourtId) return true;
      if (Number.isFinite(m.courtNumber) && String(m.courtNumber) === finishingCourtId) return true;
      return false;
    });
    if (!stillActive) {
      setFinishing(false);
      setFinishingCourtId(null);
      setFinishDialogOpen(false);
      setSelectedCourt(null);
    }
  }, [matches, finishingCourtId]);

  const formatDuration = (seconds: number) => {
    const minsTotal = Math.floor(seconds / 60);
    const mins = minsTotal.toString().padStart(2, '0');
    const secs = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };

  const parseStart = (value?: string | null) => {
    if (!value) return null;
    const hasTz = /([zZ]|[+-]\d{2}:?\d{2})$/.test(value);
    const normalized = hasTz ? value : `${value}Z`;
    const ts = Date.parse(normalized);
    return Number.isNaN(ts) ? null : ts;
  };

  const formatClock = (value?: string | null) => {
    const ts = parseStart(value);
    if (!ts) return '';
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const resolveAssignerName = (match: any) => {
    const memberId = match.assignedByMemberId || match.assigned_by_member_id;
    const userId = match.assignedById || match.assigned_by_id;
    if (memberId) {
      const m = members.find((mem) => String(mem.id) === String(memberId));
      if (m) return `${m.firstName} ${m.lastName}`.trim();
    }
    if (userId) {
      const m = members.find((mem) => String(mem.userId) === String(userId) || String(mem.id) === String(userId));
      if (m) return `${m.firstName} ${m.lastName}`.trim();
      return `User ${userId}`;
    }
    return '';
  };

  const activeMatchLookups = useMemo(
    () =>
      matches.map((match: any) => {
        const { team1, team2 } = resolveMatchTeams(match);
        const team1Ids = team1.map((id: string | number) => String(id));
        const team2Ids = team2.map((id: string | number) => String(id));
        const team1Key = normalizeTeamIds(team1Ids);
        const team2Key = normalizeTeamIds(team2Ids);
        return {
          match,
          team1Ids,
          team2Ids,
          team1Key,
          team2Key,
          lookupKey: `${buildCourtKey(match)}:${team1Key}|${team2Key}`,
        };
      }),
    [matches],
  );

  const headToHeadQueries = useQueries({
    queries: activeMatchLookups.map((lookup) => ({
      queryKey: ['head-to-head', clubId, lookup.team1Key, lookup.team2Key],
      queryFn: () =>
        matchService.getHeadToHead({
          club_id: clubId as number,
          team1_player_ids: lookup.team1Ids.map((id) => Number(id)),
          team2_player_ids: lookup.team2Ids.map((id) => Number(id)),
        }),
      enabled: Boolean(clubId && lookup.team1Ids.length && lookup.team2Ids.length),
      staleTime: 60_000,
    })),
  });

  const headToHeadByLookup = useMemo(() => {
    const map = new Map<string, any>();
    activeMatchLookups.forEach((lookup, idx) => {
      const data = headToHeadQueries[idx]?.data;
      map.set(lookup.lookupKey, data);
    });
    return map;
  }, [activeMatchLookups, headToHeadQueries]);

  const entryElapsed = (entry: typeof queue[number]) => {
    const started = parseStart(entry.waitStartedAt);
    if (started) return Math.max(0, Math.floor((now - started) / 1000));
    if (typeof entry.elapsedSeconds === 'number') return entry.elapsedSeconds;
    if (typeof entry.lastQueueSeconds === 'number') return entry.lastQueueSeconds;
    return 0;
  };

  const longestWaitIds = React.useMemo(() => {
    if (!isSkillBased || orderedQueue.length === 0) return new Set<string>();
    const sortedByWait = [...orderedQueue].sort((a, b) => entryElapsed(b) - entryElapsed(a));
    const top = sortedByWait[0];
    return top ? new Set([top.userId]) : new Set<string>();
  }, [isSkillBased, orderedQueue, entryElapsed]);

  const balancedTopWaitIds = React.useMemo(() => {
    if (!isBalanced || orderedQueue.length === 0) return new Set<string>();
    const sortedByWait = [...orderedQueue].sort((a, b) => entryElapsed(b) - entryElapsed(a));
    const topTwo = sortedByWait.slice(0, 2).map((entry) => entry.userId);
    return new Set(topTwo);
  }, [isBalanced, orderedQueue, entryElapsed]);


  // Check if current user is a player on the selected court
  const isPlayerOnCourt = (court: Court) => {
    if (!currentMember) return false;
    const memberId = String(currentMember.id);
    return court.players.some((playerId) => String(playerId) === memberId);
  };

  const canCurrentUserFinishMatch = (court: Court) => {
    if (!user) return false;
    if (currentMember?.role === 'admin' || currentMember?.role === 'coach') return true;
    return isPlayerOnCourt(court);
  };

  const resolveMemberName = (memberId: string) => {
    const member = getMemberById(memberId);
    if (!member) return '';
    return `${member.firstName || ''} ${member.lastName || ''}`.trim();
  };

  const handleAddToQueue = async (memberId: string, name?: string) => {
    if (queueLimitReached) {
      toast.error('Queue is at capacity for this club');
      return;
    }
    const isSelfAction = isSelf(memberId);
    if (!canManageQueue && !isSelfAction) {
      toast.error('Players can only add themselves to the queue');
      return;
    }
    const memberName = name?.trim() || resolveMemberName(memberId) || 'Player';

    setPendingRemovals((prev) => {
      const next = new Set(prev);
      next.delete(memberId);
      return next;
    });
    setPendingAdds((prev) => {
      return {
        ...prev,
        [memberId]: {
          firstName: memberName.split(' ')[0] || '',
          lastName: memberName.split(' ').slice(1).join(' ') || '',
          status: 'pending',
        },
      };
    });

    try {
      await setPlayerAvailable(memberId, true);
      toast.success(`${memberName} added to queue`);
      setPendingAdds((prev) => ({
        ...prev,
        [memberId]: {
          ...(prev[memberId] || { firstName: '', lastName: '' }),
          status: 'confirmed',
        },
      }));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Could not add to queue';
      toast.error(message);
      setPendingAdds((prev) => {
        const next = { ...prev };
        delete next[memberId];
        return next;
      });
    }
  };

  const handleRemoveFromQueue = async (memberId: string, name?: string) => {
    const isSelfAction = isSelf(memberId);
    if (!canManageQueue && !isSelfAction) {
      toast.error('Players can only remove themselves from the queue');
      return;
    }
    setPendingAdds((prev) => {
      const next = { ...prev };
      delete next[memberId];
      return next;
    });
    setPendingRemovals((prev) => {
      const next = new Set(prev);
      next.add(memberId);
      return next;
    });

    try {
      await setPlayerAvailable(memberId, false);
      const memberName = name?.trim() || resolveMemberName(memberId) || 'Player';
      toast.success(`${memberName} removed from queue`);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Could not remove from queue';
      toast.error(message);
      setPendingRemovals((prev) => {
        const next = new Set(prev);
        next.delete(memberId);
        return next;
      });
      return;
    }

    setPendingRemovals((prev) => {
      const next = new Set(prev);
      next.delete(memberId);
      return next;
    });
  };

  const requestRemoveFromQueue = (memberId: string, name: string) => {
    setRemoveConfirm({
      memberId,
      name: name.trim() || 'this player',
    });
  };

  const confirmRemoveFromQueue = async () => {
    if (!removeConfirm) return;
    setRemoving(true);
    try {
      await handleRemoveFromQueue(removeConfirm.memberId, removeConfirm.name);
    } finally {
      setRemoving(false);
      setRemoveConfirm(null);
    }
  };

  const confirmRemoveFromCourt = async () => {
    if (!clubId || !removeCourtConfirm) return;
    setRemovingFromCourt(true);
    try {
      await courtService.removePlayer(clubId, removeCourtConfirm.courtId, removeCourtConfirm.memberId);
      await queryClient.invalidateQueries({ queryKey: ['match-history', clubId] });
      await queryClient.invalidateQueries({ queryKey: ['club-queue', clubId] });
      setRemoveCourtConfirm(null);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to remove player from court');
    } finally {
      setRemovingFromCourt(false);
    }
  };

    const handleAssignToCourt = async (courtId: string) => {
    if (String(courtId).startsWith('virtual-')) {
      toast.error('Court IDs are not available yet. Please refresh or update courts in Club Settings.');
      return;
    }
    const targetCourt = courts.find((c) => c.id === courtId);
    if (targetCourt?.isActive) {
      toast.error('Finish the current match on this court before assigning new players');
      return;
    }
    if (queuedEntries.length < 4) {
      toast.error('Need at least 4 players in queue');
      return;
    }
    if (assigningCourts.has(courtId)) {
      toast.info('Already assigning players to this court...');
      return;
    }

    setAssigningCourts((prev) => {
      const next = new Set(prev);
      next.add(courtId);
      return next;
    });

    let assignmentSucceeded = false;
    try {
      await assignPlayersToCourt(courtId);
      assignmentSucceeded = true;
      toast.success('Players assigned to court!');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to assign players';
      toast.error(message);
    } finally {
      if (!assignmentSucceeded) {
        setAssigningCourts((prev) => {
          const next = new Set(prev);
          next.delete(courtId);
          return next;
        });
      }
    }
  };
const handleDragEnd = async (event: DragEndEvent) => {
    if (!clubId) return;
    if (isPlayersPick && isMobilePick) return;
    const active = event.active?.data?.current as CourtDragMeta | QueuePickMeta | undefined;
    const over = event.over?.data?.current as CourtDragMeta | CourtPickTargetMeta | undefined;
    setActiveDrag(null);
    if (!active || !over) return;

    if (active.kind === 'swap' && over.kind === 'swap') {
      if (!canDragSwap) return;
      if (active.courtId !== over.courtId) return;
      if (active.playerId === over.playerId) return;
      if (active.team === over.team) return;
      try {
        await courtService.swapPlayers(clubId, active.courtId, {
          playerId: active.playerId,
          targetPlayerId: over.playerId,
        });
        await queryClient.invalidateQueries({ queryKey: ['match-history', clubId] });
        toast.success('Players swapped');
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to swap players';
        toast.error(message);
      }
      return;
    }

    if (active.kind === 'pick' && over.kind === 'pick-target') {
      if (!canPickPlayers) return;
      if (!freeCourtInfo.courtId || over.courtId !== freeCourtInfo.courtId) return;
      setSelectedPickIds((prev) => {
        if (prev.includes(active.memberId)) return prev;
        if (prev.length >= 4) return prev;
        return [...prev, active.memberId];
      });
    }
  };

  const confirmQueueAction = async () => {
    if (!queueActionConfirm) return;
    setQueueActionLoading(true);
    try {
      if (queueActionConfirm === 'clear') {
        setClearingQueue(true);
        setPendingAdds({});
        setPendingRemovals(new Set());
        try {
          await clearQueue();
          toast.success('Queue cleared');
        } finally {
          setClearingQueue(false);
        }
      } else {
        if (!allowQueueShuffle) {
          toast.error('Queue shuffle is disabled for this club');
          return;
        }
        await shuffleQueue();
        toast.success('Queue shuffled');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Could not update queue';
      toast.error(message);
    } finally {
      setQueueActionLoading(false);
      setQueueActionConfirm(null);
    }
  };

  const handleSkipTopPlayer = async () => {
    if (!clubId) return;
    try {
      await queueService.skipTop(clubId);
      await queryClient.invalidateQueries({ queryKey: ['club-queue', clubId] });
      toast.success('Top player skipped');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to skip top player';
      toast.error(message);
    }
  };

  const togglePickPlayer = (memberId: string) => {
    if (!isPlayersPick) return;
    if (!canPickPlayers) return;
    if (selectedPickIds.includes(memberId)) {
      removePickedPlayer(memberId);
      return;
    }
    if (selectedPickIds.length >= 4) return;
    setSelectedPickIds((prev) => [...prev, memberId]);
  };

  const removePickedPlayer = (memberId: string) => {
    if (String(memberId) === String(pickerId)) return;
    setSelectedPickIds((prev) => prev.filter((id) => id !== memberId));
  };

  const openFinishDialog = (court: Court) => {
    const isFinishingPlayer = isPlayerOnCourt(court);
    const canFinish = canCurrentUserFinishMatch(court);

    if (!canFinish) {
      toast.error('Only players on this court or coaches/admins can finish the match');
      return;
    }

    setSelectedCourt(court);

    setScoreInput({ team1: '21', team2: '21' });

    setNotes('');

    setEquipmentOwnerId(isFinishingPlayer && user?.id ? user.id : null);
    setEquipmentUsed(isFinishingPlayer && user?.id ? [{ memberId: user.id, shoes: '', racket: '' }] : []);

    setFinishDialogOpen(true);
  };

  const score = {
    team1: scoreStrToInt(scoreInput.team1),
    team2: scoreStrToInt(scoreInput.team2),
  };

  const scoreCheck = validateBadmintonFinalScore(score.team1, score.team2);

  const handleFinishMatch = async () => {
    if (!selectedCourt) return;
    if (!canCurrentUserFinishMatch(selectedCourt)) {
      toast.error('You are not allowed to finish this match');
      return;
    }

    const check = validateBadmintonFinalScore(score.team1, score.team2);
    if (!check.ok) {
      toast.error(check.message);
      return;
    }

    setFinishing(true);
    try {
      const filteredEquipment = equipmentUsed
        .filter((eq) => eq.shoes || eq.racket)
        .map((eq) => ({
          memberId: eq.memberId,
          shoes: eq.shoes,
          racket: eq.racket,
        }));

      await finishMatch(
        String(selectedCourt.number),
        score,
        isEquipmentFeatureAvailable && equipmentOwnerId ? filteredEquipment : [],
        notes,
        user?.id,
      );
      setFinishingCourtId(selectedCourt.id);
      toast.success('Match finished and queue updated');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to finish match';
      toast.error(message);
    } finally {
      // Keep dialog and finishing state until match disappears from list
      if (!finishingCourtId) {
        setFinishing(false);
      }
    }
  };

  const updateEquipment = (memberId: string, field: 'shoes' | 'racket', value: string) => {
    const cleanValue = value === 'none' ? '' : value;
    setEquipmentUsed((prev) => prev.map((e) => (e.memberId === memberId ? { ...e, [field]: cleanValue } : e)));
  };

  if (!clubId) {
    return <PageSpinner label="Loading queue..." />;
  }

  if (clubQuery.isLoading || membersQuery.isLoading || queueQuery.isLoading || historyQuery.isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <PageHeaderSkeleton />
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-1 space-y-4">
          <SectionCardSkeleton lines={6} />
        </div>
        <div className="xl:col-span-2 space-y-4">
          <CardGridSkeleton count={6} className="grid grid-cols-1 sm:grid-cols-2 gap-4" />
        </div>
      </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-foreground flex flex-wrap items-center gap-3">
            <ListOrdered className="text-primary shrink-0" />
            <span className="break-words">Queue & Courts ({selectionLabel})</span>
          </h1>
        </div>
      </div>

      {/* Layout */}
      <DndContext
        sensors={dndSensors}
        onDragStart={(event) => {
          const data = event.active?.data?.current as CourtDragMeta | QueuePickMeta | undefined;
          setActiveDrag(data || null);
        }}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveDrag(null)}
      >
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Queue Section */}
        <div className="xl:col-span-1 space-y-4 order-2 xl:order-1">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center justify-between gap-2 queue-header">
                <span className="flex items-center gap-2">
                  <Users size={20} />
                  Queue ({queuedEntries.length})
                </span>

                {canManageQueue && (
                  <div className="flex gap-2 queue-actions">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setQueueActionConfirm('shuffle')}
                        disabled={!allowQueueShuffle || orderedQueue.length === 0 || queueActionLoading}
                      >
                        Shuffle
                      </Button>

                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setQueueActionConfirm('clear')}
                      disabled={orderedQueue.length === 0 || queueActionLoading}
                    >
                      Clear
                    </Button>
                  </div>
                )}
              </CardTitle>
            </CardHeader>

            <CardContent className="space-y-2">
              {canManageQueue && (
                <div className="flex items-center gap-2">
                    <Input
                      placeholder="Search Members..."
                      value={queueSearch}
                      onChange={(e) => setQueueSearch(e.target.value)}
                      className="h-9"
                  />

                </div>
              )}

              {isPlayersPick && (
                <div className="rounded-lg border bg-muted/40 px-3 py-2 text-xs text-muted-foreground space-y-1">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-foreground">Players Pick Mode</p>
                      <p>
                        Picker:{' '}
                        {pickerMember
                          ? `${pickerMember.firstName} ${pickerMember.lastName}`
                          : orderedQueue.length
                            ? 'Waiting for top player'
                            : 'No players in queue'}
                      </p>
                      <p>
                        Court:{' '}
                        {pickCourtNumber ? `Court ${pickCourtNumber}` : 'Waiting for an open court'}
                      </p>
                      {pickTimedOut && (
                        <p className="text-amber-600">Top player timed out. Next player can pick.</p>
                      )}
                    </div>
                    {canSkipTop && (
                      <Button variant="outline" size="sm" onClick={handleSkipTopPlayer}>
                        Skip Top Player
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {filteredQueue.length > 0 ? (
                <div className="space-y-2 max-h-[60vh] lg:max-h-[70vh] overflow-y-auto pr-1">
                  {filteredQueue.map((entry, index) => {
                    const member = getMemberById(entry.userId);
                  const isPriority = isRoundRobin && !!entry.priorityCandidate;
                  const isCandidate = isRoundRobin && roundRobinPoolIds.includes(entry.userId);
                  const isPicked = isPlayersPick && selectedPickIds.includes(entry.userId);
                  const isLongestWait =
                    (isSkillBased && longestWaitIds.has(entry.userId)) ||
                    (isBalanced && balancedTopWaitIds.has(entry.userId));
                  const displayName = `${entry.firstName || member?.firstName || ''} ${entry.lastName || member?.lastName || ''}`.trim() || 'this player';
                  const avatarUrl = resolveAvatarUrl(member);
                  const avatarAlt =
                    member?.avatarName ||
                    `${entry.firstName || member?.firstName || ''} ${entry.lastName || member?.lastName || ''}`.trim() ||
                    'Player avatar';
                  const avatarFallback = getInitials(entry.firstName || member?.firstName, entry.lastName || member?.lastName);

                  const queueHighlight = isPicked
                    ? 'border-primary bg-primary/10 shadow-[0_0_0_1px_rgba(59,130,246,0.25)]'
                    : isPriority
                    ? 'border-amber-300 bg-amber-50/80 shadow-[0_0_0_1px_rgba(251,191,36,0.35)]'
                    : isCandidate
                      ? 'border-primary/30 bg-primary/5'
                        : isLongestWait
                          ? 'border-emerald-300 bg-emerald-50/80 shadow-[0_0_0_1px_rgba(16,185,129,0.25)]'
                          : 'border-transparent';

                    return (
                      <QueuePickItem
                        entryId={entry.userId}
                        member={member}
                        canDrag={isPlayersPick && canPickPlayers && !isMobilePick}
                      >
                        <div
                          className={`queue-item queue-item-animate flex items-center justify-between gap-3 border ${queueHighlight} rounded-lg px-3 py-2`}
                          style={{ transitionDelay: `${index * 30}ms` }}
                          role={isPlayersPick && isMobilePick ? 'button' : undefined}
                          tabIndex={isPlayersPick && isMobilePick ? 0 : -1}
                          onClick={
                            isPlayersPick && isMobilePick
                              ? () => togglePickPlayer(entry.userId)
                              : undefined
                          }
                          onKeyDown={
                            isPlayersPick && isMobilePick
                              ? (event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    togglePickPlayer(entry.userId);
                                  }
                                }
                              : undefined
                          }
                        >
                          <div className="queue-person flex items-center gap-3 min-w-0">
                            <Avatar className="queue-avatar h-10 w-10 shrink-0">
                              {avatarUrl && <AvatarImage src={avatarUrl} alt={avatarAlt} />}
                              <AvatarFallback>{avatarFallback}</AvatarFallback>
                            </Avatar>

                            <div className="queue-person-details min-w-0">
                              <p className="name-line font-medium flex items-center gap-2 flex-wrap">
                                <span className="truncate max-w-[200px] sm:max-w-[260px]">
                                  {(entry.firstName || member?.firstName || '')}{' '}
                                  {(entry.lastName || member?.lastName || '')}
                                </span>

                                {member?.role === 'coach' && (
                                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
                                    Coach
                                  </span>
                                )}
                                {member?.role === 'admin' && (
                                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-800">
                                    Admin
                                  </span>
                                )}
                                {isPriority && (
                                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                                    Priority
                                  </span>
                                )}
                                {!isPriority && isCandidate && (
                                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                                    Candidate
                                  </span>
                                )}
                              </p>

                              <div className="text-xs text-muted-foreground flex items-center gap-2 mt-1">
                                {renderStars(getMemberRating(member, isEvaluationsFeatureAvailable) ?? 0)}
                              </div>
                              <p className="text-xs text-muted-foreground font-mono">
                                Time in Queue: {formatDuration(entryElapsed(entry))}
                              </p>
                            </div>
                          </div>

                          {(canManageQueue || isSelf(entry.userId)) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="shrink-0 queue-remove-btn"
                              onClick={() => requestRemoveFromQueue(entry.userId, displayName)}
                              aria-label="Remove from queue"
                            >
                              <X size={16} />
                            </Button>
                          )}
                        </div>
                      </QueuePickItem>
                    );
                  })}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4">
                  {queueSearch.trim() ? 'No players match your search.' : 'No players in queue'}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Add to Queue */}
          {addableMembers.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Plus size={18} />
                  Add to Queue
                </CardTitle>
              </CardHeader>

              <CardContent className="space-y-2">
                {canManageQueue && (
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="Search members..."
                      value={addSearch}
                      onChange={(e) => setAddSearch(e.target.value)}
                      className="h-9"
                    />

                  </div>
                )}

                <div className="max-h-[50vh] overflow-y-auto pr-1 space-y-2">
                  {filteredAddableMembers.map((member) => (
                    <div
                      key={member.id}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-2 rounded bg-muted/50"
                    >
                      <div className="add-queue-person flex items-center gap-3 min-w-0">
                        <Avatar className="queue-avatar h-10 w-10 shrink-0">
                          {resolveAvatarUrl(member) && (
                            <AvatarImage
                              src={resolveAvatarUrl(member)}
                              alt={member.avatarName || `${member.firstName} ${member.lastName}`}
                            />
                          )}
                          <AvatarFallback>{getInitials(member.firstName, member.lastName)}</AvatarFallback>
                        </Avatar>

                        <div className="min-w-0">
                          <p className="name-line font-medium flex items-center gap-2 flex-wrap">
                          <span className="truncate">
                            {member.firstName} {member.lastName}
                          </span>
                          <span className="flex items-center gap-1">
                            {renderStars(getMemberRating(member, isEvaluationsFeatureAvailable) ?? 0)}
                          </span>
                          {member.role === 'coach' && (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
                              Coach
                            </span>
                          )}
                          {member.role === 'admin' && (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-800">
                              Admin
                            </span>
                          )}
                          </p>
                        </div>
                      </div>

                      <Button
                        size="sm"
                        className="w-full sm:w-auto add-to-queue-btn"
                        onClick={() => handleAddToQueue(member.id, `${member.firstName} ${member.lastName}`)}
                        disabled={queueLimitReached}
                      >
                        Add
                      </Button>
                    </div>
                  ))}
                </div>

                {filteredAddableMembers.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center">
                    {addSearch.trim() ? 'No members match your search.' : 'No members available to add.'}
                  </p>
                )}

                {queueLimitReached && (
                  <p className="text-xs text-destructive mt-2">Queue is full (limit {queueLimit}).</p>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Courts Section */}
        <div className="xl:col-span-2 order-1 xl:order-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {courts
              .slice((courtPage - 1) * courtsPerPage, courtPage * courtsPerPage)
              .map((court) => {
                const canFinishMatch = canCurrentUserFinishMatch(court);
                const activeMatch = court.isActive
                  ? (matches.find(
                    (m: any) =>
                      (m as any).courtId === court.id ||
                        (!m.courtId && (m as any).courtNumber === court.number && m.isActive),
                  ) as any)
                  : null;
                const matchTeams = activeMatch ? resolveMatchTeams(activeMatch) : { team1: [], team2: [] };
                const lookupKey = activeMatch
                  ? `${buildCourtKey(activeMatch)}:${normalizeTeamIds(matchTeams.team1)}|${normalizeTeamIds(matchTeams.team2)}`
                  : '';
                const headToHeadData = lookupKey ? headToHeadByLookup.get(lookupKey) : null;
                const matchSelectionName =
                  selectionLabelFromValue(activeMatch?.selectionMechanism) || selectionName;
                const headToHeadSummary = headToHeadData
                  ? {
                      leadingIsTeam1: headToHeadData.team1_win_pct >= headToHeadData.team2_win_pct,
                      winPct:
                        headToHeadData.team1_win_pct >= headToHeadData.team2_win_pct
                          ? headToHeadData.team1_win_pct
                          : headToHeadData.team2_win_pct,
                      total: headToHeadData.total_matches,
                      team1Wins: headToHeadData.team1_wins,
                      team2Wins: headToHeadData.team2_wins,
                    }
                  : null;
                const pickCountRemaining = Math.max(0, 4 - selectedPickIds.length);
                const pickedMembers = selectedPickIds
                  .map((id) => getMemberById(id))
                  .filter((m): m is Member => Boolean(m));
                const canShowPickUi = isPlayersPick && pickCourtId && court.id === pickCourtId;
                const canDropPick = canPickPlayers && canShowPickUi && queuedEntries.length >= 4;
                return (
                  <Card key={court.id} className={`overflow-hidden ${court.isActive ? 'ring-2 ring-destructive' : ''}`}>
                    <CardHeader className="pb-2">
                      <CardTitle className="relative flex items-center justify-between gap-2">
                        <span className="absolute left-1/2 -translate-x-1/2 text-center pointer-events-none">
                          Court {court.number}
                        </span>
                        {court.isActive ? (
                          <span className="text-xs bg-destructive text-destructive-foreground px-2 py-1 rounded-full flex items-center gap-1 shrink-0">
                            <Timer size={12} /> In Progress
                          </span>
                        ) : (
                          <span className="text-xs bg-success text-success-foreground px-2 py-1 rounded-full shrink-0 tracking-wide">
                            Available
                          </span>
                        )}
                      </CardTitle>
                    </CardHeader>

                    <CardContent>
                      {court.isActive ? (
                        <div className="space-y-4">
                          {activeMatch && (
                            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-center text-xs font-semibold text-amber-800 animate-pulse">
                              {headToHeadSummary && headToHeadSummary.total > 0 ? (() => {
                                const team1Names = (matchTeams.team1 || []).map((id) => {
                                  const player = getMemberById(String(id));
                                  return player ? `${player.firstName} ${player.lastName[0]}.` : `Player ${id}`;
                                }).join(' & ');
                                const team2Names = (matchTeams.team2 || []).map((id) => {
                                  const player = getMemberById(String(id));
                                  return player ? `${player.firstName} ${player.lastName[0]}.` : `Player ${id}`;
                                }).join(' & ');
                                const leadingIsTeam1 = headToHeadSummary.leadingIsTeam1;
                                const winningNames = leadingIsTeam1 ? team1Names : team2Names;
                                const losingNames = leadingIsTeam1 ? team2Names : team1Names;
                                const winningCount = leadingIsTeam1 ? headToHeadSummary.team1Wins : headToHeadSummary.team2Wins;
                                return `${winningNames} won ${headToHeadSummary.winPct}% (${winningCount}/${headToHeadSummary.total}) of the time against ${losingNames}`;
                              })() : 'No prior head to head'}
                            </div>
                          )}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div className="space-y-2 text-center">
                                <p className="text-base font-semibold text-foreground">Team 1</p>
                                {(court.team1 || court.players.slice(0, 2)).map((playerId) => {
                                  const player = getMemberById(playerId);
                                  return (
                                    player && (
                                      <CourtPlayerChip
                                        key={playerId}
                                        courtId={court.id}
                                        player={player}
                                        team={1}
                                        canDrag={canDragSwap}
                                        canManageQueue={canManageQueue}
                                        ratingValue={getMemberRating(player, isEvaluationsFeatureAvailable) ?? 0}
                                        onRemove={() =>
                                          setRemoveCourtConfirm({
                                            memberId: playerId,
                                            name: `${player.firstName} ${player.lastName}`,
                                            courtId: court.id,
                                          })
                                        }
                                      />
                                    )
                                  );
                                })}
                              </div>

                              <div className="space-y-2 text-center">
                                <p className="text-base font-semibold text-foreground">Team 2</p>
                                {(court.team2 || court.players.slice(2, 4)).map((playerId) => {
                                  const player = getMemberById(playerId);
                                  return (
                                    player && (
                                      <CourtPlayerChip
                                        key={playerId}
                                        courtId={court.id}
                                        player={player}
                                        team={2}
                                        canDrag={canDragSwap}
                                        canManageQueue={canManageQueue}
                                        ratingValue={getMemberRating(player, isEvaluationsFeatureAvailable) ?? 0}
                                        onRemove={() =>
                                          setRemoveCourtConfirm({
                                            memberId: playerId,
                                            name: `${player.firstName} ${player.lastName}`,
                                            courtId: court.id,
                                          })
                                        }
                                      />
                                    )
                                  );
                                })}
                              </div>
                          </div>

                          <div className="text-sm text-slate-600 flex flex-col items-center gap-2 text-center">
                            {(() => {
                              if (!activeMatch) return null;
                              const startedStr = activeMatch.startedAt || activeMatch.timestamp;
                              const startedTs = parseStart(startedStr);
                              const elapsed =
                                startedTs && now
                                  ? Math.max(0, Math.floor((now - startedTs) / 1000))
                                  : null;
                              const assignedBy =
                                resolveAssignerName(activeMatch) ||
                                activeMatch.recordedBy ||
                                '';
                              return (
                                <>
                                  <span>
                                    <span className="font-semibold">Time Elapsed:</span>{' '}
                                    {elapsed !== null ? `${formatDuration(elapsed)} (MM:SS)` : '—'}
                                  </span>
                                  {assignedBy && (
                                    <span>
                                      <span className="font-semibold">Assigned by:</span> {assignedBy}
                                    </span>
                                  )}
                                  <span>
                                    <span className="font-semibold">Selection Criteria:</span> {matchSelectionName}
                                  </span>
                                </>
                              );
                            })()}
                          </div>

                          {canFinishMatch && (
                            <Button
                              onClick={() => openFinishDialog(court)}
                              className="w-full hover:bg-sidebar-primary hover:text-white"
                              variant="outline"
                            >
                              <CheckCircle size={18} className="mr-2" />
                              Finish Match
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="py-6 text-center min-h-[250px] flex flex-col justify-center">
                          {assigningCourts.has(court.id) ? (
                            <div className="space-y-3">
                              <Skeleton className="h-4 w-28 mx-auto" />
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-center">
                                <div className="space-y-2 text-center">
                                  <p className="text-base font-semibold text-foreground">Team 1</p>
                                  {Array.from({ length: 2 }).map((_, idx) => (
                                    <Skeleton key={`t1-${idx}`} className="h-8 w-full" />
                                  ))}
                                </div>
                                <div className="space-y-2 text-center">
                                  <p className="text-base font-semibold text-foreground">Team 2</p>
                                  {Array.from({ length: 2 }).map((_, idx) => (
                                    <Skeleton key={`t2-${idx}`} className="h-8 w-full" />
                                  ))}
                                </div>
                              </div>
                              <Skeleton className="h-9 w-40 mx-auto" />
                            </div>
                          ) : (
                            <>
                              {isPlayersPick ? (
                                <div className="space-y-3">
                                  {canShowPickUi ? (
                                    <CourtPickDropZone courtId={court.id} isActive={canDropPick}>
                                      <div className="space-y-3 p-3">
                                        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                                          <span>
                                      {canPickPlayers
                                        ? pickCountRemaining > 0
                                          ? isMobilePick
                                            ? `Tap ${pickCountRemaining} more player${pickCountRemaining > 1 ? 's' : ''} in the queue.`
                                            : `Pick ${pickCountRemaining} more player${pickCountRemaining > 1 ? 's' : ''} from the queue.`
                                          : 'All players selected. Assigning shortly...'
                                        : pickerMember
                                          ? `Waiting for ${pickerMember.firstName} ${pickerMember.lastName} to pick players.`
                                                : 'Waiting for the top player to pick.'}
                                          </span>
                                          {pickCountRemaining === 0 && (
                                            <span className="text-emerald-600 font-medium">Ready</span>
                                          )}
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                          {Array.from({ length: 4 }).map((_, idx) => {
                                            const member = pickedMembers[idx];
                                            if (!member) {
                                              return (
                                                <div
                                                  key={`pick-slot-${idx}`}
                                                  className="rounded-md border border-dashed px-3 py-3 text-xs text-muted-foreground"
                                                >
                                                  Drop player here
                                                </div>
                                              );
                                            }
                                            const isPicker = String(member.id) === String(pickerId);
                                            return (
                                              <div
                                                key={`pick-member-${member.id}`}
                                                className="flex items-center justify-between gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm"
                                              >
                                                <div className="flex items-center gap-2 min-w-0">
                                                  <Avatar className="h-8 w-8 shrink-0">
                                                    {resolveAvatarUrl(member) && (
                                                      <AvatarImage
                                                        src={resolveAvatarUrl(member)}
                                                        alt={member.avatarName || `${member.firstName} ${member.lastName}`}
                                                      />
                                                    )}
                                                    <AvatarFallback className="text-[10px]">
                                                      {getInitials(member.firstName, member.lastName)}
                                                    </AvatarFallback>
                                                  </Avatar>
                                                  <div className="min-w-0">
                                                    <p className="truncate">
                                                      {member.firstName} {member.lastName}
                                                    </p>
                                                    <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                                                      {renderStars(getMemberRating(member, isEvaluationsFeatureAvailable) ?? 0)}
                                                      {isPicker && <span className="text-amber-600">Picker</span>}
                                                    </div>
                                                  </div>
                                                </div>
                                                {canPickPlayers && !isPicker && (
                                                  <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-7 w-7"
                                                    onClick={() => removePickedPlayer(member.id)}
                                                    aria-label="Remove picked player"
                                                  >
                                                    <X size={14} />
                                                  </Button>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>

                                        {queuedEntries.length < 4 && (
                                          <p className="text-xs text-muted-foreground">
                                            {t('queue_need_more_players', 'Need {{count}} more players in queue').replace('{{count}}', String(4 - queuedEntries.length))}
                                          </p>
                                        )}
                                      </div>
                                    </CourtPickDropZone>
                                  ) : (
                                    <div className="text-xs text-muted-foreground">
                                      {pickCourtNumber
                                        ? `Waiting for Court ${pickCourtNumber} to be filled before using this court.`
                                        : 'Waiting for a court to become available.'}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <>
                                  {canManageQueue && (
                                    <Button
                                      className="w-full sm:w-auto hover:bg-sidebar-primary hover:text-white"
                                      onClick={() => handleAssignToCourt(court.id)}
                                      disabled={court.isActive || queuedEntries.length < 4 || assigningCourts.has(court.id)}
                                      variant="outline"
                                    >
                                      {assigningCourts.has(court.id) ? `Assigning${assignDots}` : 'Assign Players'}
                                    </Button>
                                  )}

                                  {queuedEntries.length < 4 && (
                                    <p className="text-xs text-muted-foreground mt-2">
                                      {t('queue_need_more_players', 'Need {{count}} more players in queue').replace('{{count}}', String(4 - queuedEntries.length))}
                                    </p>
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
          </div>

          {courts.length > courtsPerPage && (
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between text-sm text-muted-foreground">
              <span>
                Showing {(courtPage - 1) * courtsPerPage + 1}-{Math.min(courtPage * courtsPerPage, courts.length)} of{' '}
                {courts.length}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 sm:flex-none"
                  onClick={() => setCourtPage((p) => Math.max(1, p - 1))}
                  disabled={courtPage === 1}
                >
                  Prev
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 sm:flex-none"
                  onClick={() => setCourtPage((p) => (p * courtsPerPage < courts.length ? p + 1 : p))}
                  disabled={courtPage * courtsPerPage >= courts.length}
                >
                  Next
                </Button>
              </div>
            </div>
          )}

          {courts.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">No courts configured. Set up courts in Club settings.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      <DragOverlay>
        {activeDrag ? (
          (() => {
            if (activeDrag.kind === 'swap') {
              const player = getMemberById(activeDrag.playerId);
              return player ? <CourtPlayerChipOverlay player={player} /> : null;
            }
            if (activeDrag.kind === 'pick') {
              const player = getMemberById(activeDrag.memberId);
              return player ? <CourtPlayerChipOverlay player={player} /> : null;
            }
            return null;
          })()
        ) : null}
      </DragOverlay>
      </DndContext>

      <Dialog
        open={!!removeConfirm}
        onOpenChange={(open) => {
          if (!open && !removing) setRemoveConfirm(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove from queue?</DialogTitle>
            <DialogDescription>
              You are about to remove {removeConfirm?.name || 'this player'} from the queue. Are you sure?
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setRemoveConfirm(null)} disabled={removing}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmRemoveFromQueue} disabled={removing}>
              {removing ? 'Removing...' : 'Remove'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!removeCourtConfirm}
        onOpenChange={(open) => {
          if (!open && !removingFromCourt) setRemoveCourtConfirm(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove from court?</DialogTitle>
            <DialogDescription>
              You are about to remove {removeCourtConfirm?.name || 'this player'} from the court. They will be placed at the end of the queue. Continue?
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setRemoveCourtConfirm(null)} disabled={removingFromCourt}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmRemoveFromCourt} disabled={removingFromCourt}>
              {removingFromCourt ? 'Removing...' : 'Remove'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!queueActionConfirm}
        onOpenChange={(open) => {
          if (!open && !queueActionLoading) setQueueActionConfirm(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
              <DialogTitle>{queueActionConfirm === 'clear' ? 'Clear the queue?' : 'Shuffle the queue?'}</DialogTitle>
              <DialogDescription>
                {queueActionConfirm === 'clear'
                  ? 'This will remove all queued players. Are you sure you want to reset the queue?'
                  : 'This will randomize the current queue order. Are you sure you want to shuffle the queue?'}
              </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setQueueActionConfirm(null)} disabled={queueActionLoading}>
              Cancel
            </Button>
            <Button
              onClick={confirmQueueAction}
              disabled={queueActionLoading}
              variant={queueActionConfirm === 'clear' ? 'destructive' : 'default'}
            >
              {queueActionLoading ? 'Processing...' : 'Yes, continue'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Finish Match Dialog */}
      <Dialog open={finishDialogOpen} onOpenChange={setFinishDialogOpen}>
        <DialogContent className="max-w-lg w-[95vw] sm:w-full max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Finish Match - Court {selectedCourt?.number}</DialogTitle>
            <DialogDescription>Enter the final score and any match notes.</DialogDescription>
          </DialogHeader>

          {finishing ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8">
                <div className="space-y-2 text-center">
                  <Skeleton className="h-4 w-24 mx-auto" />
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-3 w-32 mx-auto" />
                </div>
                <div className="space-y-2 text-center">
                  <Skeleton className="h-4 w-24 mx-auto" />
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-3 w-32 mx-auto" />
                </div>
              </div>
              <Skeleton className="h-4 w-44 mx-auto" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (
            <div className="space-y-6">
            {/* Score Input */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8">
              <div className="text-center space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Team 1</p>
                <Select
                  value={scoreInput.team1}
                  onValueChange={(v) => setScoreInput((prev) => ({ ...prev, team1: v }))}
                >
                  <SelectTrigger className="h-16 text-3xl font-bold text-center justify-center">
                    <SelectValue placeholder="0" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 31 }).map((_, i) => (
                      <SelectItem key={i} value={String(i)}>
                        {i}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="text-xs text-muted-foreground">
                  {(selectedCourt?.team1 || selectedCourt?.players.slice(0, 2) || [])
                    .map((id) => {
                      const p = getMemberById(id);
                      return p ? `${p.firstName} ${p.lastName[0]}.` : '';
                    })
                    .join(' & ')}
                </div>
              </div>

              <div className="text-center space-y-2">
                <p className="text-sm font-medium text-muted-foreground">Team 2</p>
                <Select
                  value={scoreInput.team2}
                  onValueChange={(v) => setScoreInput((prev) => ({ ...prev, team2: v }))}
                >
                  <SelectTrigger className="h-16 text-3xl font-bold text-center justify-center">
                    <SelectValue placeholder="0" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 31 }).map((_, i) => (
                      <SelectItem key={i} value={String(i)}>
                        {i}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="text-xs text-muted-foreground">
                  {(selectedCourt?.team2 || selectedCourt?.players.slice(2, 4) || [])
                    .map((id) => {
                      const p = getMemberById(id);
                      return p ? `${p.firstName} ${p.lastName[0]}.` : '';
                    })
                    .join(' & ')}
                </div>
              </div>
            </div>

            {/* Validation message */}
            {!scoreCheck.ok && <p className="text-sm text-destructive text-center">{scoreCheck.message}</p>}

            {/* Equipment Used - Only show to the player finishing the match */}
            {isEquipmentFeatureAvailable && equipmentOwnerId && selectedCourt && (
              <div className="space-y-3">
                <Label>Equipment Used (Optional)</Label>
                <div className="text-sm text-muted-foreground mb-2">
                  You can optionally record which equipment was used in this match
                </div>

                {(selectedCourt.players || [])
                  .filter((pid) => pid === equipmentOwnerId)
                  .map((playerId) => {
                    const player = getMemberById(playerId);
                    if (!player) return null;

                    const playerEquipment = player.equipment || [];
                    const shoes = playerEquipment.filter((e) => e.type === 'shoes');
                    const rackets = playerEquipment.filter((e) => e.type === 'racket');

                    return (
                      <div key={playerId} className="p-3 bg-muted rounded-lg space-y-2">
                        <p className="text-sm font-medium">
                          {player.firstName} {player.lastName}
                        </p>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <Label className="text-xs">Shoes</Label>
                            <Select onValueChange={(v) => updateEquipment(playerId, 'shoes', v)}>
                              <SelectTrigger className="h-9 text-sm">
                                <SelectValue placeholder="Select shoes..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">None selected</SelectItem>
                                {shoes.map((eq) => (
                                  <SelectItem key={eq.id} value={String(eq.id)}>
                                    {eq.name || eq.make} - {eq.manufacturer}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-1">
                            <Label className="text-xs">Racket</Label>
                            <Select onValueChange={(v) => updateEquipment(playerId, 'racket', v)}>
                              <SelectTrigger className="h-9 text-sm">
                                <SelectValue placeholder="Select racket..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">None selected</SelectItem>
                                {rackets.map((eq) => (
                                  <SelectItem key={eq.id} value={String(eq.id)}>
                                    {eq.name || eq.make} - {eq.manufacturer}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any notes about the match..."
                rows={3}
              />
            </div>

            <Button
              onClick={handleFinishMatch}
              className="w-full hover:bg-sidebar-primary hover:text-white"
              disabled={!scoreCheck.ok || finishing}
              variant="outline"
            >
              <CheckCircle size={18} className="mr-2" />
              {finishing ? `Finish${finishDots}` : 'Finish'}
            </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
